import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc, collection, query, where, limit, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useAuth } from "../context/AuthContext";
import ProductCard from "../components/shop/ProductCard";
import ReviewSection from "../components/reviews/ReviewSection";
import SlimAdBanner from "../components/ui/SlimAdBanner";
import { ShoppingCart, Heart, Star, Shield, Truck, RotateCcw, Minus, Plus } from "lucide-react";
import { formatPrice } from "../utils/format";
import toast from "react-hot-toast";

export default function ProductDetailPage() {
  const { id } = useParams();
  const { addToCart } = useCart();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState({});
  const [imgZoom, setImgZoom] = useState(false);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, "products", id));
        if (snap.exists()) {
          const p = { id: snap.id, ...snap.data() };
          setProduct(p);

          // Track recently viewed
          const RV_KEY = "afriport_recently_viewed";
          const rv = JSON.parse(localStorage.getItem(RV_KEY) || "[]");
          const updated = [id, ...rv.filter(x => x !== id)].slice(0, 8);
          localStorage.setItem(RV_KEY, JSON.stringify(updated));

          // Load related products
          const relSnap = await getDocs(query(
            collection(db, "products"),
            where("category", "==", p.category),
            where("status", "==", "active"),
            limit(5)
          ));
          setRelated(relSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(d => d.id !== id));

          // Load recently viewed (excluding current)
          const rvIds = updated.slice(1, 5);
          if (rvIds.length) {
            const rvProducts = await Promise.all(
              rvIds.map(vid => getDoc(doc(db, "products", vid)).then(s => s.exists() ? { id: s.id, ...s.data() } : null))
            );
            setRecentlyViewed(rvProducts.filter(Boolean));
          }

          // Init first variant options
          if (p.variants) {
            const init = {};
            Object.keys(p.variants).forEach(k => { init[k] = p.variants[k][0]?.value; });
            setSelectedVariant(init);
          }
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
    setActiveImg(0);
    setQty(1);
  }, [id]);

  if (loading) return (
    <div style={{ padding: "60px 0" }}>
      <div className="container">
        <div className="pdp-main-grid">
          <div className="skeleton" style={{ aspectRatio: "1", borderRadius: 16 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="skeleton" style={{ height: 20, width: "30%" }} />
            <div className="skeleton" style={{ height: 36, width: "80%" }} />
            <div className="skeleton" style={{ height: 36, width: "60%" }} />
            <div className="skeleton" style={{ height: 48, width: "40%" }} />
            <div className="skeleton" style={{ height: 120 }} />
          </div>
        </div>
      </div>
    </div>
  );

  if (!product) return (
    <div style={{ textAlign: "center", padding: 80 }}>
      Product not found. <Link to="/shop" style={{ color: "var(--brand)" }}>Back to shop</Link>
    </div>
  );

  const wishlisted = isWishlisted(product.id);

  async function handleWishlist() {
    if (!user) { toast.error("Sign in to save items"); return; }
    const added = await toggleWishlist(product.id);
    toast.success(added ? "Saved to wishlist ❤️" : "Removed from wishlist");
  }

  function handleAdd() {
    for (let i = 0; i < qty; i++) {
      addToCart({
        id: product.id,
        name: product.name + (Object.keys(selectedVariant).length ? " · " + Object.values(selectedVariant).join(" / ") : ""),
        price: product.price,
        image: product.images?.[0] || "",
        category: product.category,
        subcategory: product.subcategory || "",
        variant: selectedVariant,
      });
    }
    toast.success(`${qty}× added to cart`);
  }

  const discount = product.originalPrice ? Math.round((1 - product.price / product.originalPrice) * 100) : null;
  const isLowStock = product.stock > 0 && product.stock <= 5;

  return (
    <div style={{ background: "#fff" }}>
      {/* Lightbox */}
      {imgZoom && (
        <div onClick={() => setImgZoom(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out" }}>
          <img src={product.images[activeImg]} alt="" style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 12 }} />
          <button onClick={() => setImgZoom(false)}
            style={{ position: "absolute", top: 20, right: 24, border: "none", background: "rgba(255,255,255,0.15)", color: "#fff", borderRadius: "50%", width: 40, height: 40, fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>
      )}

      <div className="container" style={{ padding: "clamp(20px, 4vw, 40px) clamp(14px, 3vw, 24px)" }}>
        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32, fontSize: 13, color: "var(--text-muted)", flexWrap: "wrap" }}>
          <Link to="/" style={{ color: "var(--text-muted)" }}>Home</Link> /
          <Link to="/shop" style={{ color: "var(--text-muted)" }}>Shop</Link> /
          {product.category && (
            <>
              <Link to={`/shop?category=${encodeURIComponent(product.category)}`} style={{ color: "var(--text-muted)" }}>{product.category}</Link> /
            </>
          )}
          {product.subcategory && (
            <>
              <Link to={`/shop?category=${encodeURIComponent(product.category)}&subcategory=${encodeURIComponent(product.subcategory)}`} style={{ color: "var(--text-muted)" }}>{product.subcategory}</Link> /
            </>
          )}
          <span style={{ color: "var(--text)", fontWeight: 500 }}>{product.name.slice(0, 40)}{product.name.length > 40 ? "…" : ""}</span>
        </div>

        {/* Main grid */}
        <div className="pdp-main-grid">
          {/* Images */}
          <div>
            <div className="img-zoom-wrap" onClick={() => product.images?.length && setImgZoom(true)}
              style={{ aspectRatio: "1", background: "var(--surface)", borderRadius: 20, overflow: "hidden", border: "1px solid var(--border)", cursor: "zoom-in", marginBottom: 12 }}>
              {product.images?.[activeImg] ? (
                <img src={product.images[activeImg]} alt={product.name} loading="lazy"
                  style={{ width: "100%", height: "100%", objectFit: "contain", padding: 20 }} />
              ) : (
                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 60 }}>📦</div>
              )}
            </div>
            {product.images?.length > 1 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {product.images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)}
                    style={{ width: 68, height: 68, border: `2px solid ${i === activeImg ? "var(--brand)" : "var(--border)"}`, borderRadius: 10, overflow: "hidden", padding: 0, background: "#fff", cursor: "pointer", transition: "border-color 0.2s" }}>
                    <img src={img} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </button>
                ))}
              </div>
            )}
            <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 8, textAlign: "center" }}>Click image to zoom</p>
          </div>

          {/* Product info */}
          <div>
            <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
              <Link to={`/shop?category=${encodeURIComponent(product.category)}`} style={{ fontSize: 12, color: "var(--brand)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", textDecoration: "none" }}>{product.category}</Link>
              {product.subcategory && (
                <Link to={`/shop?category=${encodeURIComponent(product.category)}&subcategory=${encodeURIComponent(product.subcategory)}`} style={{ fontSize: 11, background: "var(--brand-light)", color: "var(--brand)", padding: "2px 8px", borderRadius: 4, fontWeight: 600, textDecoration: "none" }}>
                  {product.subcategory}
                </Link>
              )}
              {product.brand && <span style={{ fontSize: 12, color: "var(--text-muted)" }}>· {product.brand}</span>}
            </div>

            <h1 style={{ fontFamily: "var(--font-head)", fontSize: 26, fontWeight: 700, marginBottom: 14, lineHeight: 1.2 }}>{product.name}</h1>

            {/* Rating */}
            {product.rating > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
                {[1,2,3,4,5].map(s => <Star key={s} size={16} fill={s <= Math.round(product.rating) ? "#F59E0B" : "none"} color="#F59E0B" />)}
                <span style={{ fontWeight: 600, fontSize: 14 }}>{product.rating}</span>
                <a href="#reviews" style={{ fontSize: 13, color: "var(--brand)" }}>({product.reviewCount || 0} reviews)</a>
              </div>
            )}

            {/* Price */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
                <span style={{ fontFamily: "var(--font-head)", fontSize: 36, fontWeight: 800, color: "var(--text)" }}>{formatPrice(product.price)}</span>
                {product.originalPrice && (
                  <span style={{ fontSize: 18, color: "var(--text-muted)", textDecoration: "line-through" }}>{formatPrice(product.originalPrice)}</span>
                )}
                {discount && (
                  <span style={{ background: "var(--brand)", color: "#fff", fontSize: 13, fontWeight: 700, padding: "3px 10px", borderRadius: 100 }}>Save {discount}%</span>
                )}
              </div>
            </div>

            <p style={{ fontSize: 15, color: "var(--text-muted)", lineHeight: 1.8, marginBottom: 24 }}>{product.description}</p>

            {/* Variants */}
            {product.variants && Object.entries(product.variants).map(([type, options]) => (
              <div key={type} style={{ marginBottom: 18 }}>
                <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, textTransform: "capitalize" }}>
                  {type}: <span style={{ color: "var(--brand)", fontWeight: 700 }}>{selectedVariant[type]}</span>
                </p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {options.map(opt => (
                    <button key={opt.value} onClick={() => setSelectedVariant(v => ({ ...v, [type]: opt.value }))}
                      className={`variant-pill${selectedVariant[type] === opt.value ? " selected" : ""}`}
                      disabled={opt.stock === 0}>
                      {opt.value}
                      {opt.priceAdj ? ` (+${formatPrice(opt.priceAdj)})` : ""}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {/* Key specs preview */}
            {product.specs && Object.keys(product.specs).length > 0 && (
              <div style={{ padding: 16, background: "var(--surface)", borderRadius: 12, marginBottom: 20 }}>
                <p style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)", marginBottom: 10 }}>Key Specs</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 140px), 1fr))", gap: "6px 16px" }}>
                  {Object.entries(product.specs).slice(0, 6).map(([k, v]) => (
                    <div key={k} style={{ fontSize: 13 }}>
                      <span style={{ color: "var(--text-muted)" }}>{k}: </span>
                      <span style={{ fontWeight: 600 }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stock status */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: product.stock > 0 ? "var(--success)" : "var(--danger)", flexShrink: 0 }} />
              <span style={{ fontSize: 14, fontWeight: 500, color: product.stock > 0 ? "var(--success)" : "var(--danger)" }}>
                {product.stock > 0 ? `In Stock` : "Out of Stock"}
              </span>
              {isLowStock && <span className="urgency-badge urgency-low">Only {product.stock} left!</span>}
            </div>

            {/* Qty + Add to cart */}
            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", border: "1.5px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
                <button onClick={() => setQty(q => Math.max(1, q - 1))}
                  style={{ width: 42, height: 48, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Minus size={16} />
                </button>
                <span style={{ width: 42, textAlign: "center", fontWeight: 700, fontSize: 16 }}>{qty}</span>
                <button onClick={() => setQty(q => Math.min(product.stock, q + 1))}
                  style={{ width: 42, height: 48, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Plus size={16} />
                </button>
              </div>
              <button onClick={handleAdd} className="btn btn-primary btn-lg" style={{ flex: 1 }} disabled={product.stock <= 0}>
                <ShoppingCart size={18} /> {product.stock <= 0 ? "Out of Stock" : "Add to Cart"}
              </button>
              <button onClick={handleWishlist}
                style={{ width: 48, height: 48, border: `1.5px solid ${wishlisted ? "#DC2626" : "var(--border)"}`, borderRadius: 10, background: wishlisted ? "#FEE2E2" : "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Heart size={20} fill={wishlisted ? "#DC2626" : "none"} color={wishlisted ? "#DC2626" : "var(--text-muted)"} />
              </button>
            </div>

            {/* Trust badges */}
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap", padding: "16px 0", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)", marginBottom: 20 }}>
              {[[Shield, "Warranty Included"], [Truck, "Fast Nationwide Delivery"], [RotateCcw, "30-Day Returns"]].map(([Icon, text]) => (
                <div key={text} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-muted)" }}>
                  <Icon size={15} color="var(--brand)" /> {text}
                </div>
              ))}
            </div>

            {/* Tags */}
            {product.tags?.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {product.tags.map(tag => (
                  <Link key={tag} to={`/shop?search=${encodeURIComponent(tag)}`}
                    style={{ fontSize: 12, padding: "3px 10px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 100, color: "var(--text-muted)" }}>
                    #{tag}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Slim Ad Banner placement (Product Detail) */}
        <SlimAdBanner placement="product_detail" style={{ marginBottom: 40 }} />

        {/* Full specs */}
        {product.specs && Object.keys(product.specs).length > 0 && (
          <div style={{ marginBottom: 56 }}>
            <h2 style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 700, marginBottom: 20 }}>Full Specifications</h2>
            <div className="card" style={{ overflow: "hidden" }}>
              <table className="compare-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  {Object.entries(product.specs).map(([k, v], i) => (
                    <tr key={k} style={{ background: i % 2 === 0 ? "#fff" : "var(--surface)" }}>
                      <th style={{ padding: "12px 20px", textAlign: "left", fontSize: 14, color: "var(--text-muted)", fontWeight: 600, width: "30%" }}>{k}</th>
                      <td style={{ padding: "12px 20px", fontSize: 14, fontWeight: 500 }}>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Reviews */}
        <div id="reviews" style={{ marginBottom: 56 }}>
          <ReviewSection productId={id} />
        </div>

        {/* Related products */}
        {related.length > 0 && (
          <div style={{ marginBottom: 56 }}>
            <h2 style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 700, marginBottom: 24 }}>Related Products</h2>
            <div className="product-grid">
              {related.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}

        {/* Recently viewed */}
        {recentlyViewed.length > 0 && (
          <div>
            <h2 style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 700, marginBottom: 24 }}>Recently Viewed</h2>
            <div className="product-grid">
              {recentlyViewed.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
